import type { Stream, StreamsResponse } from '../types/Stream';

function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isDevelopment) {
    return 'http://localhost:3001';
  }

  return window.location.origin;
}

const API_BASE_URL = getApiBaseUrl();

class StreamerService {
  /**
   * Récupère tous les streamers actuellement en direct
   * Endpoint public - pas d'authentification requise
   */
  async getLiveStreamers(): Promise<StreamsResponse> {
    try {
      const apiUrl = `${API_BASE_URL}/api/streamers/live`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des streamers:', error);
      throw new Error('Impossible de récupérer les streamers en direct');
    }
  }

  /**
   * Calcule les statistiques totales des streams
   */
  calculateStats(streams: Stream[]) {
    const totalStreamers = streams.length;
    const totalViewers = streams.reduce((sum, stream) => sum + stream.viewerCount, 0);
    
    return {
      totalStreamers,
      totalViewers,
    };
  }

  /**
   * Filtre les streams selon une recherche
   */
  filterStreams(streams: Stream[], searchTerm: string): Stream[] {
    if (!searchTerm.trim()) {
      return streams;
    }

    const term = searchTerm.toLowerCase();
    
    return streams.filter(stream => 
      stream.displayName.toLowerCase().includes(term) ||
      stream.username.toLowerCase().includes(term) ||
      stream.title.toLowerCase().includes(term) ||
      stream.gameName.toLowerCase().includes(term)
    );
  }

  /**
   * Formate la durée depuis le début du stream
   */
  getStreamDuration(startedAt: string): string {
    const start = new Date(startedAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - start.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    }
    
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    
    if (hours < 24) {
      return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    return `${days}j${remainingHours > 0 ? ` ${remainingHours}h` : ''}`;
  }

  /**
   * Formate le nombre de viewers
   */
  formatViewerCount(count: number): string {
    if (count < 1000) {
      return count.toString();
    }
    
    if (count < 1000000) {
      const k = Math.floor(count / 100) / 10;
      return `${k}k`;
    }
    
    const m = Math.floor(count / 100000) / 10;
    return `${m}M`;
  }
}

export default new StreamerService();